import { useEffect, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { Box, CssBaseline } from "@mui/material";

import SideBar from "./Sidebar";
import AppBar from "./AppBar";
import { useDispatch, useSelector } from "react-redux";
import { Profile } from "../../app/slices/profileSlice";
import Cookies from "js-cookie";
import useFetch from "../../hooks/useFetch";
import OneSignal from "react-onesignal";

// async function runOneSignal(userId) {
//   await OneSignal.init({
//     appId: process.env.REACT_APP_ONE_SIGNAL_APP_ID,
//     // allowLocalhostAsSecureOrigin: true,
//     // notifyButton: {
//     //   enable: true,
//     // },
//     // serviceWorkerPath: "/OneSignalSDKWorker.js",
//     // serviceWorkerUpdaterPath: "/OneSignalSDKUpdaterWorker.js",
//   });
//   await OneSignal.Slidedown.promptPush();
//   await OneSignal.login(userId);
//   await OneSignal.User.PushSubscription.optIn();
// }

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    backgroundColor: "#FAFAFA",
    flexGrow: 1,
    // marginTop: 100,
    width: "100%",
    minHeight: "calc(100vh - 80px)",
    // flexGrow: 1,
    padding: "10px",
    marginTop: "75px",
    // marginRight: '20px',
    borderRadius: `8px`,
    ...(!open && {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    }),
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    }),
  })
);

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const leftDrawerOpened = useSelector((state) => state.app.is_drawer_opened);
  const { user } = useSelector((state) => state.profile);
  const token = Cookies.get("ticket_hala_system");
  const [initialized, setInitialized] = useState(false);
  const [ticketStatistics, setTicketStatistics] = useState({});
  const [reminders, setReminders] = useState([]);
  const [reminderCount, setReminderCount] = useState(0);
  const [openTasksCount, setOpenTasksCount] = useState(0);
  const [inProgressTasksCount, setInProgressTasksCount] = useState(0);
  
  // fetch all permissions
  // useFetch("/role/permissions", {
  //   onSuccess: (res) => {
  //     dispatch(
  //       setPermissions({
  //         total: res.data.total,
  //         data: res.data.data,
  //       })
  //     );
  //   },
  // });
  const { refetch: refetchTotalTickets } = useFetch("/ticket-statistics", {
    onSuccess: (res) => {
      setTicketStatistics({
        total: res.data.total,
        data: res.data.data,
      });
      setReminderCount(res.data.data?.ticket_reminders_count || 0);
    },
  });
  
  // Unfinished tasks badge (open + in progress). The endpoint scopes itself: this
  // is "my tasks" for a user without task_view, and "tasks in my departments" with it.
  const { refetch: refetchOpenTasks } = useFetch("/tasks?status=open&take=1", {
    onSuccess: (res) => {
      setOpenTasksCount(res.data.total || 0);
    },
  });
  const { refetch: refetchInProgressTasks } = useFetch(
    "/tasks?status=in_progress&take=1",
    {
      onSuccess: (res) => {
        setInProgressTasksCount(res.data.total || 0);
      },
    }
  );

  // Fetch reminders
  const { refetch: refetchReminders } = useFetch("/ticket-reminder", {
    onSuccess: (res) => {
      const fetchedReminders = res.data.data || [];
      setReminders(fetchedReminders);
    },
    onError: (error) => {
      console.error("Failed to fetch reminders:", error);
    },
  });
  // fetch all roles
  // useFetch("/role", {
  //   onSuccess: (res) => {
  //     dispatch(
  //       setRoles({
  //         roles: res.data.data,
  //       })
  //     );
  //   },
  // });

  useEffect(() => {
    if (user.info.id === "") {
      dispatch(Profile.myInfo());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const interval = setInterval(() => {
      refetchTotalTickets();
      refetchOpenTasks();
      refetchInProgressTasks();
    }, 60000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll reminders every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      refetchReminders();
    }, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // useEffect(() => {
  //   console.log("==========runOneSignal effect===============");
  //   if (!initialized && user.info?.id) {
  //     console.log("==========runOneSignal inside if===============");
  //     runOneSignal(user.info.id)
  //       .then(() => {
  //         console.log("==========runOneSignal success===============");
  //         setInitialized(true);
  //       })
  //       .catch((e) => console.log("runOneSignal error", e));
  //   }
  // }, [user.info?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const initializeOneSignal = async () => {
      try {
        await OneSignal.init({
          appId: process.env.REACT_APP_ONE_SIGNAL_APP_ID,
          allowLocalhostAsSecureOrigin: true,
          notifyButton: {
            enable: true,
          },
          serviceWorkerParam: {
            scope: "/OneSignalSDKWorker.js",
          },
          serviceWorkerPath: "/OneSignalSDKWorker.js",
          serviceWorkerUpdaterPath: "/OneSignalSDKUpdaterWorker.js",
        });

        setInitialized(true);
        await OneSignal.login(user.info?.id);
      } catch (error) {
        console.error("OneSignal initialization error:", error);
      }
    };

    if (!initialized && user.info?.id) {
      initializeOneSignal();
    }
  }, [initialized, user.info?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return token ? (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar 
        reminders={reminders}
        reminderCount={reminderCount}
        onRefetchReminders={refetchReminders}
      />
      <SideBar
        totalTickets={ticketStatistics?.data?.my_ticket_count}
        totalRequests={ticketStatistics?.data?.ticket_items_request}
        totalItemRequests={ticketStatistics?.data?.item_request_stats}
        totalTasks={openTasksCount + inProgressTasksCount}
      />
      <Main open={leftDrawerOpened}>
        <Outlet />
      </Main>
    </Box>
  ) : (
    <Navigate 
      to={`/login?redirect_url=${encodeURIComponent(location.pathname + location.search)}`} 
      replace={true} 
    />
  );
};

export default MainLayout;
